import {
  InvoiceReadable,
  invoicesListOptions,
  invoicesReportsExportCreateMutation,
  invoicesReportsRetrieveOptions
} from '@crm/api'
import { AppLink } from '@crm/components'
import {
  ReportsFilters,
  TotalReportsCard
} from '@crm/features/billing/components'
import { useCheckedList } from '@crm/hooks/useCheckedList'
import { getRoute, TagBillingStatuses } from '@crm/utils'
import {
  ConditionalText,
  DayLabel,
  TableContainer,
  TitleSection
} from '@shared/components'
import { useQueryParams, useTableSorter } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { defaultFilters, downloadFile } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  Col,
  Flex,
  notification,
  Row,
  Tag,
  Tooltip
} from 'antd'

import { useMemo } from 'react'

import type { TableColumnsType } from 'antd'

export const ReportsPage = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()

  const { data: reports, isLoading: isLoadingReports } = useQuery({
    ...invoicesReportsRetrieveOptions({})
  })

  const { data, isLoading } = useQuery({
    ...invoicesListOptions({ query: { ...defaultFilters, ...params } })
  })

  const { mutate } = useMutation({
    ...invoicesReportsExportCreateMutation({ responseType: 'blob' }),
    onSuccess: (data) => {
      notification.success({
        message: t('success.success'),
        description: 'Export finshed!'
      })

      downloadFile(data, 'Invoices-reports.xlsx')
    }
  })

  const {
    checkedItems: checkedLeads,
    isCheckedAllItems: isCheckedAllLeads,
    onItemCheck: onReportCheck,
    onAllCheck
  } = useCheckedList(data?.results?.map((i) => i.id) || [])

  const columns = useMemo<TableColumnsType<InvoiceReadable>>(
    () => [
      {
        title: <Checkbox onChange={onAllCheck} checked={isCheckedAllLeads} />,
        render: ({ id }): React.ReactNode => (
          <Checkbox
            onChange={(e) => {
              onReportCheck(e.target.checked, id)
            }}
            checked={
              isCheckedAllLeads ||
              Boolean(checkedLeads.find((item) => item === id))
            }
          />
        )
      },
      {
        title: t('billing.invoice'),
        dataIndex: 'invoice_number',
        key: 'invoice_number',
        sorter: true,
        render: (invoiceNumber, { id }) =>
          invoiceNumber && id ? (
            <AppLink to={getRoute('BillingInvoicesDetails', { id })}>
              {invoiceNumber}
            </AppLink>
          ) : (
            '-'
          )
      },
      {
        title: t('billing.contractor'),
        dataIndex: 'lead',
        key: 'lead__name',
        sorter: true,
        render: (lead) =>
          lead?.id && lead?.name ? (
            <AppLink to={`/leads/${lead.id}`}>{lead.name}</AppLink>
          ) : (
            '-'
          )
      },
      {
        title: t('billing.title'),
        dataIndex: 'title',
        key: 'title',
        sorter: true
      },
      {
        title: t('billing.product'),
        dataIndex: ['product', 'name'],
        key: 'product__name',
        sorter: true,
        render: (productName) => (
          <ConditionalText condition={!!productName}>
            {productName}
          </ConditionalText>
        )
      },
      {
        title: t('billing.created'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (createdAt) => <DayLabel date={createdAt} />
      },
      {
        title: t('billing.due_date'),
        dataIndex: 'due_date',
        key: 'due_date',
        sorter: true,
        render: (dueDate) => <DayLabel date={dueDate} />
      },
      {
        title: t('billing.pay_date'),
        dataIndex: 'pay_date',
        render: (pay_date) => (
          <ConditionalText condition={!!pay_date}>{pay_date}</ConditionalText>
        )
      },
      {
        title: t('billing.amount'),
        key: 'total_amount',
        sorter: true,
        dataIndex: 'total_amount'
      },
      {
        title: t('billing.status'),
        dataIndex: 'status',
        key: 'status',
        sorter: true,
        render: (status) =>
          status ? (
            <Tag
              color={
                TagBillingStatuses[status as keyof typeof TagBillingStatuses]
              }
            >
              {t(`billing.${status}`)}
            </Tag>
          ) : (
            '-'
          )
      }
    ],
    [checkedLeads, isCheckedAllLeads, t, onAllCheck, onReportCheck]
  )

  const enableExport = useMemo(
    () => Boolean(checkedLeads.length || isCheckedAllLeads),
    [checkedLeads, isCheckedAllLeads]
  )

  const onExportClick = () => mutate({ body: { ids: checkedLeads } })

  return (
    <>
      <TitleSection
        title={t('reports.reports')}
        count={data?.count}
        isLoading={isLoading}
      />

      <Flex vertical gap={12}>
        <Row gutter={[20, 12]}>
          {['total_invoices', 'total_paid', 'total_unpaid', 'total_amount'].map(
            (i) => (
              <Col key={i} span={24} sm={12} xl={6}>
                <TotalReportsCard
                  title={t(`billing.${i}`)}
                  value={reports?.[i as keyof typeof reports]}
                  loading={isLoadingReports}
                />
              </Col>
            )
          )}
        </Row>

        <TableContainer
          loading={isLoading}
          data={data}
          columns={columns}
          onChange={(_, __, result) => sorter(result)}
          showSorterTooltip={false}
          isHoverAlternative
          leftChildren={<ReportsFilters />}
          rightChildren={
            <Tooltip
              title={enableExport ? null : t('billing.select_rows')}
              placement="left"
            >
              <Button
                type="primary"
                onClick={onExportClick}
                disabled={!enableExport}
              >
                {t('buttons.export')}
              </Button>
            </Tooltip>
          }
        />
      </Flex>
    </>
  )
}
